import React, { useEffect, useState } from "react";
import { CheckCircle2, Loader2, MailWarning, ShieldCheck } from "lucide-react";
import { onAuthStateChanged, User } from "firebase/auth";
import { auth } from "../Lib/Firebase";
import { activateEnrollmentInvitation } from "../Lib/enrollmentApi";
import { openAuthModal } from "../StaticComponents/AuthModal";

const ACTIVATION_TOKEN_KEY = "mockzo_activation_token";

export default function ActivationPage() {
    const [user, setUser] = useState<User | null>(auth.currentUser);
    const [state, setState] = useState<"checking" | "sign-in" | "activating" | "done" | "error">("checking");
    const [message, setMessage] = useState("");

    useEffect(() => onAuthStateChanged(auth, setUser), []);

    useEffect(() => {
        const token = new URLSearchParams(window.location.search).get("token") || window.localStorage.getItem(ACTIVATION_TOKEN_KEY) || "";
        if (!token) {
            setState("error");
            setMessage("This activation link is incomplete. Ask your administrator to send a new one.");
            return;
        }
        window.localStorage.setItem(ACTIVATION_TOKEN_KEY, token);
        if (!user) {
            setState("sign-in");
            openAuthModal("student");
            return;
        }
        let active = true;
        setState("activating");
        activateEnrollmentInvitation(token)
            .then(() => {
                if (!active) return;
                window.localStorage.removeItem(ACTIVATION_TOKEN_KEY);
                setState("done");
                setMessage("Your enrollment is active. Redirecting to your dashboard…");
                window.setTimeout(() => window.location.replace("/dashboard"), 1200);
            })
            .catch((error: any) => {
                if (!active) return;
                setState("error");
                setMessage(error?.message || "We could not activate this enrollment.");
            });
        return () => { active = false; };
    }, [user]);

    const isWorking = state === "checking" || state === "activating";
    return <main className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10 text-slate-900">
        <section className="w-full max-w-lg rounded-3xl border border-blue-100 bg-white p-7 text-center shadow-xl shadow-blue-900/10 sm:p-10">
            {isWorking ? <Loader2 className="mx-auto h-12 w-12 animate-spin text-blue-600" /> : state === "done" ? <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" /> : state === "sign-in" ? <ShieldCheck className="mx-auto h-12 w-12 text-blue-600" /> : <MailWarning className="mx-auto h-12 w-12 text-amber-600" />}
            <h1 className="mt-5 text-2xl font-black text-[#061a4a]">{state === "done" ? "Enrollment activated" : state === "sign-in" ? "Sign in to activate" : state === "error" ? "Activation unavailable" : "Checking your invitation"}</h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">{message || (state === "sign-in" ? "Use the same email address that received the invitation. The sign-in window is open now." : "Please keep this page open while we securely activate your enrollment.")}</p>
            {(state === "sign-in" || state === "error") && <button type="button" onClick={() => openAuthModal("student")} className="mt-6 rounded-xl bg-blue-700 px-5 py-3 text-sm font-black text-white">Sign in with your invited email</button>}
        </section>
    </main>;
}
